import { useEffect, useState } from 'react';
import { WorkspaceAPI } from '../../../../../utils/api';
import { buildAcceptInviteUrl } from '../../../../../utils/acceptInviteUrl';

export function useInviteFlow({
  workspaceId,
  workspaceName,
  isPersonal,
  user,
  myRole,
  mayManagePeople,
  invitesNotWired,
  startCheckout,
  pending,
  setPending,
}) {
  const [email, setEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('user');
  const [copiedId, setCopiedId] = useState(null);
  const [inviteBusy, setInviteBusy] = useState(false);
  const [actionError, setActionError] = useState('');
  const [cancellingId, setCancellingId] = useState(null);
  const [seatPrompt, setSeatPrompt] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);

  useEffect(() => {
    setEmail('');
    setInviteOpen(false);
    setSeatPrompt(null);
    setActionError('');
  }, [workspaceId, isPersonal, user?.sub, user?.email, myRole, mayManagePeople]);

  const markCopied = (id) => {
    setCopiedId(id);
    window.setTimeout(() => setCopiedId((cur) => (cur === id ? null : cur)), 1600);
  };

  const copyInviteLink = async (invite) => {
    const url = buildAcceptInviteUrl(workspaceId, invite.id, {
      email: invite.email,
      workspaceName,
    });
    try {
      await navigator.clipboard?.writeText?.(url);
      markCopied(invite.id);
      return true;
    } catch {
      return false;
    }
  };

  const finishInvite = async (created) => {
    if (!created?.id) {
      throw new Error('Invite was not created.');
    }
    setPending((prev) => [created, ...prev.filter((p) => p.id !== created.id)]);
    setEmail('');
    setInviteOpen(false);
    await copyInviteLink(created);
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || inviteBusy) return;
    if (invitesNotWired) {
      setActionError('Invite from a named brain — not leftover Personal.');
      return;
    }
    const existing = pending.find(
      (inv) => (inv.email || '').trim().toLowerCase() === trimmed
    );
    if (existing) {
      setActionError('That email already has an invite waiting. Link copied again.');
      copyInviteLink(existing);
      return;
    }
    setInviteBusy(true);
    setActionError('');
    setSeatPrompt(null);
    try {
      const result = await WorkspaceAPI.createInvite(workspaceId, {
        email: trimmed,
        role: inviteRole,
      });
      await finishInvite(result?.invite);
    } catch (err) {
      if (err?.code === 'NEED_SEAT') {
        setSeatPrompt({
          email: trimmed,
          role: inviteRole,
          seats: Number(err.seats) > 0 ? Number(err.seats) : 1,
        });
      } else {
        setActionError(err?.message || 'Could not create invite.');
      }
    } finally {
      setInviteBusy(false);
    }
  };

  const confirmAddSeat = async () => {
    if (!seatPrompt || inviteBusy) return;
    setInviteBusy(true);
    setActionError('');
    try {
      const added = await startCheckout('team', workspaceId, { addSeat: true });
      if (!added?.addedSeat && !added?.switched) {
        throw new Error('Could not add a seat.');
      }
      const retry = await WorkspaceAPI.createInvite(workspaceId, {
        email: seatPrompt.email,
        role: seatPrompt.role,
      });
      await finishInvite(retry?.invite);
      setSeatPrompt(null);
    } catch (err) {
      setActionError(err?.message || 'Could not add a seat.');
    } finally {
      setInviteBusy(false);
    }
  };

  const handleCancelInvite = async (invite) => {
    if (!invite?.id || cancellingId) return;
    setCancellingId(invite.id);
    setActionError('');
    try {
      await WorkspaceAPI.deleteInvite(workspaceId, invite.id);
      setPending((prev) => prev.filter((x) => x.id !== invite.id));
    } catch (err) {
      setActionError(err?.message || 'Could not cancel invite.');
    } finally {
      setCancellingId(null);
    }
  };

  return {
    email,
    setEmail,
    inviteRole,
    setInviteRole,
    copiedId,
    inviteBusy,
    actionError,
    setActionError,
    cancellingId,
    seatPrompt,
    setSeatPrompt,
    inviteOpen,
    setInviteOpen,
    copyInviteLink,
    handleInvite,
    confirmAddSeat,
    handleCancelInvite,
  };
}
